import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Paper,
  Fade,
  Zoom,
  IconButton,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  School,
  Computer,
  Schedule,
  AttachMoney,
  CheckCircle,
  People,
  ArrowForward,
  Star,
  Facebook,
  Instagram,
  Twitter,
  Phone,
  Email,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import ChatBot from '../components/ChatBot';

const courses = [
  {
    title: 'Basic Computers',
    icon: <Computer />,
    description: 'Master fundamental computer skills essential for the modern workplace.',
  },
  {
    title: 'Excel',
    icon: <Computer />,
    description: 'Learn advanced Excel techniques for data analysis and visualization.',
  },
  {
    title: 'Tally Prime',
    icon: <Computer />,
    description: 'Get hands-on experience with accounting software used by businesses.',
  },
  {
    title: 'Spoken English',
    icon: <School />,
    description: 'Enhance your communication skills for professional success.',
  },
  {
    title: 'Python',
    icon: <Computer />,
    description: 'Learn programming with Python, perfect for beginners and advanced users.',
  },
  {
    title: 'Full Stack Development',
    icon: <Computer />,
    description: 'Become a complete web developer with front-end and back-end skills.',
  },
];

const features = [
  {
    icon: <School sx={{ fontSize: 40 }} />,
    title: 'Experienced Trainers',
    description: 'Learn from industry experts with years of practical experience',
  },
  {
    icon: <Computer sx={{ fontSize: 40 }} />,
    title: 'Hands-on Training',
    description: 'Get practical experience with real-world projects',
  },
  {
    icon: <People sx={{ fontSize: 40 }} />,
    title: '10000+ Students Trust Us',
    description: 'Join our growing community of successful professionals',
  },
  {
    icon: <Schedule sx={{ fontSize: 40 }} />,
    title: 'Flexible Timings',
    description: '24/7 availability to suit your schedule',
  },
  {
    icon: <AttachMoney sx={{ fontSize: 40 }} />,
    title: 'Affordable Fees',
    description: 'Quality education at competitive prices',
  },
  {
    icon: <Star sx={{ fontSize: 40 }} />,
    title: 'Certification',
    description: 'Industry-recognized certifications from top providers',
  },
];

function Home() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Contact Header */}
      <AppBar 
        position="static" 
        color="primary" 
        sx={{ 
          bgcolor: 'primary.dark',
          height: '40px',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone sx={{ fontSize: '1rem' }} />
              <Typography variant="body2">+91 9740579800</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email sx={{ fontSize: '1rem' }} />
              <Typography variant="body2">tansakeducation@gmail.com</Typography>
            </Box>
          </Box>
        </Container>
      </AppBar>

      {/* Hero Section with Gradient Animation */}
      <Box
        sx={{
          background: 'linear-gradient(-45deg, #2196F3, #21CBF3, #2196F3, #21CBF3)',
          backgroundSize: '400% 400%',
          animation: 'gradient 15s ease infinite',
          color: 'white',
          py: { xs: 8, md: 15 },
          position: 'relative',
          '@keyframes gradient': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  Upskill Global Technologies
                </Typography>
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ mb: 4, textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}
                >
                  Empowering Your Future Through Technology
                </Typography>
                <Box sx={{ mt: 4 }}>
                  <Button
                    component={Link}
                    to="/user-login"
                    variant="contained"
                    color="secondary"
                    size="large"
                    sx={{
                      mr: 2,
                      mb: 2,
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 8px rgba(0,0,0,0.2)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Get Started
                  </Button>
                  <Button
                    component={Link}
                    to="/admin-login"
                    variant="outlined"
                    color="inherit"
                    size="large"
                    sx={{
                      mb: 2,
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Admin Portal
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Box>

      {/* Vision & Mission */}
      <Container maxWidth="lg" sx={{ my: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Zoom in timeout={800}>
              <Card 
                elevation={3} 
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                    Our Vision
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    To empower individuals globally through cutting-edge technology and innovative training,
                    bridging the skills gap and enabling personal and professional growth for a brighter,
                    more inclusive future.
                  </Typography>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
          <Grid item xs={12} md={6}>
            <Zoom in timeout={1000}>
              <Card 
                elevation={3} 
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                    Our Mission
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    We are dedicated to transforming education by delivering world-class,
                    industry-relevant training that equips individuals with practical skills needed
                    to excel in a rapidly evolving digital world.
                  </Typography>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        </Grid>
      </Container>

      {/* Why Choose Us */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 600, mb: 6 }}
          >
            Why People Choose Us
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in timeout={600 + index * 100}>
                  <Card 
                    elevation={2}
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box 
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2,
                          color: 'primary.main',
                        }}
                      >
                        {feature.icon}
                        <Typography 
                          variant="h5" 
                          sx={{ ml: 2, fontWeight: 600 }}
                        >
                          {feature.title}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Courses */}
      <Container maxWidth="lg" sx={{ my: 8 }}>
        <Typography 
          variant="h3" 
          align="center" 
          gutterBottom
          sx={{ fontWeight: 600, mb: 6 }}
        >
          Our Courses
        </Typography>
        <Grid container spacing={4}>
          {courses.map((course, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Zoom in timeout={600 + index * 100}>
                <Card 
                  elevation={2}
                  sx={{
                    height: '100%',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {course.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                      {course.title}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
                      {course.description}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      endIcon={<ArrowForward />}
                      sx={{
                        mt: 2,
                        '&:hover': {
                          transform: 'translateX(5px)',
                        },
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* About Us */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 600, mb: 6 }}
          >
            About Us
          </Typography>
          <Paper 
            elevation={3}
            sx={{ 
              p: 4,
              borderRadius: 4,
              background: 'white',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              },
            }}
          >
            <Typography variant="body1" paragraph align="center" sx={{ fontSize: '1.2rem', mb: 4 }}>
              Upskill Global Technologies is the most innovative Training and Recruitment Company
              strategically located in Bangalore, delivering classroom and online trainings across India.
            </Typography>
            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3, fontWeight: 600 }}>
              What we focus on:
            </Typography>
            <List>
              {[
                'Achieving Sample exposure',
                'Revolutionising the standard of learning and development',
                'Addressing on-the-job challenges',
                'Providing tailored training solutions',
                'E-Learning concept',
                'Integration of classroom face-to-face learning experiences',
              ].map((item, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <CheckCircle color="primary" sx={{ fontSize: 24 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item} 
                    primaryTypographyProps={{ 
                      fontSize: '1.1rem',
                      fontWeight: 500 
                    }} 
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        sx={{ 
          bgcolor: '#1a237e', 
          color: 'white',
          pt: 8,
          pb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Head Office */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Head Office
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Door No: 1181, 2nd Floor,
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                24th Cross, HSR Layout Sector 2,
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Bangalore, Karnataka - 560102
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Above Ganesh Medicals
              </Typography>
            </Grid>

            {/* Contact */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Contact Us
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Mobile: +91 9740579800
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Mobile: +91 9740576800
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Email: tansakeducation@gmail.com
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <IconButton 
                  color="inherit" 
                  component="a" 
                  href="https://facebook.com" 
                  target="_blank"
                  sx={{ 
                    '&:hover': { 
                      transform: 'translateY(-3px)',
                      color: '#1877f2'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Facebook />
                </IconButton>
                <IconButton 
                  color="inherit" 
                  component="a" 
                  href="https://instagram.com" 
                  target="_blank"
                  sx={{ 
                    '&:hover': { 
                      transform: 'translateY(-3px)',
                      color: '#e4405f'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Instagram />
                </IconButton>
                <IconButton 
                  color="inherit" 
                  component="a" 
                  href="https://twitter.com" 
                  target="_blank"
                  sx={{ 
                    '&:hover': { 
                      transform: 'translateY(-3px)',
                      color: '#1da1f2'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Twitter />
                </IconButton>
              </Box>
            </Grid>

            {/* Our Partners */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Our Partners
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Namma Compu
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Tansak IT Solutions
              </Typography>
            </Grid>
          </Grid>

          {/* Copyright */}
          <Box 
            sx={{ 
              borderTop: '1px solid rgba(255,255,255,0.1)', 
              mt: 4, 
              pt: 4,
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              {new Date().getFullYear()} Upskill Global Technologies. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
      <ChatBot />
    </Box>
  );
}

export default Home;
